import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Autocomplete,
} from "@mui/material";
import { RxCross2 } from "react-icons/rx";
import { FiCheck, FiX } from "react-icons/fi";
import { toast } from "react-toastify";

const languages = [
  { value: "Hindi", label: "Hindi" },
  { value: "English", label: "English" },
  { value: "Both", label: "Both" },
];

const allModule = [
  { value: "Profile", label: "Profile" },
  { value: "Job Post", label: "Job Post" },
  { value: "Profile Tracker", label: "Profile Tracker" },
  { value: "Submission Tracker", label: "Submission Tracker" },
  { value: "Search Candidate", label: "Search Candidate" },
  { value: "Mass Mailing", label: "Mass Mailing" },
  { value: "Hot List", label: "Hot List" },
  { value: "Helpdesk", label: "Helpdesk" },
  { value: "Invite", label: "Invite" },
];

const allModulesBenchSale = [
  { value: "Profile", label: "Profile" },
  { value: "Candidate Listing", label: "Candidate Listing" },
  { value: "Hot Openings", label: "Hot Openings" },
  { value: "Submission Tracker", label: "Submission Tracker" },
  { value: "Hot List", label: "Hot List" },
  { value: "Helpdesk", label: "Helpdesk" },
  { value: "Invite", label: "Invite" },
];

const MAX_IMAGES = 3;
const MAX_IMAGE_SIZE_KB = 500; // 500KB
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_KB * 1024; // 500KB = 512,000 bytes

const DrawerContent = ({
  formType,
  setFormType,
  formik,
  multipleImages,
  setMultipleImages,
  bugData,
  setBugData,
  handleImageUploadForPublic,
  handleImageUpload,
  handleBugSubmit,
  handleBugChange,
  handleClose,
  helpdeskStatus,
  profile,
}) => {
  const handleFormTypeChange = useCallback(
    (event, newFormType) => {
      if (newFormType !== null) {
        setFormType(newFormType);
      }
    },
    [setFormType]
  );


  // State for mobile number, language preference, and drag-and-drop
  const [isMobileEditable, setIsMobileEditable] = useState(false);
  const [isLanguageEditable, setIsLanguageEditable] = useState(false);

  const fileInputRef = useRef(null);
  const [tempMobileNo, setTempMobileNo] = useState(
    formik.values.mobile_no || ""
  );
  const [tempLanguage, setTempLanguage] = useState(
    formik.values.language || ["English"]
  );
  const [isDragging, setIsDragging] = useState(false);
  const isLanguageInitialized = useRef(false);
  useEffect(() => {
    if (isLanguageInitialized.current) return;
    try {
      const savedLanguage = localStorage.getItem("languagePreference");
      if (savedLanguage) {
        const parsed = JSON.parse(savedLanguage);
        formik.setFieldValue("language", parsed);
        setTempLanguage(parsed);
      } else {
        formik.setFieldValue("language", ["English"]);
        setTempLanguage(["English"]);
      }
      isLanguageInitialized.current = true;
    } catch (error) {}
  }, [formik]);

  const handleMobileEdit = useCallback(() => {
    setTempMobileNo(formik.values.mobile_no || "");
    setIsMobileEditable(true);
  }, [formik.values.mobile_no]);

  const handleLanguageEdit = useCallback(() => {
    setTempLanguage(formik.values.language || ["English"]);
    setIsLanguageEditable(true);
  }, [formik.values.language]);

  const validateMobileNumber = useCallback((mobileNo) => {
    if (!mobileNo) return true; // Allow empty mobile number
    return /^\d{10}$/.test(mobileNo);
  }, []);

  const handleMobileSave = useCallback(() => {
    if (validateMobileNumber(tempMobileNo)) {
      formik.setFieldValue("mobile_no", tempMobileNo);
      formik.setFieldError("mobile_no", undefined);
      setIsMobileEditable(false);
    } else {
      formik.setFieldError("mobile_no", "Mobile number must be 10 digits");
    }
  }, [formik, tempMobileNo, validateMobileNumber]);

  const handleMobileCancel = useCallback(() => {
    setTempMobileNo(formik.values.mobile_no || "");
    formik.setFieldError("mobile_no", undefined);
    setIsMobileEditable(false);
  }, [formik]);

  const handleMobileBlur = useCallback(() => {
    if (validateMobileNumber(tempMobileNo)) {
      formik.setFieldValue("mobile_no", tempMobileNo);
      formik.setFieldError("mobile_no", undefined);
      setIsMobileEditable(false);
    } else {
      formik.setFieldError("mobile_no", "Mobile number must be 10 digits");
    }
  }, [formik, tempMobileNo, validateMobileNumber]);

  const handleLanguageSave = useCallback(() => {
    if (tempLanguage.length === 0) {
      formik.setFieldError(
        "language",
        "At least one language must be selected"
      );
    } else {
      formik.setFieldValue("language", tempLanguage);
      localStorage.setItem("languagePreference", JSON.stringify(tempLanguage));
      formik.setFieldError("language", undefined);
      setIsLanguageEditable(false);
    }
  }, [formik, tempLanguage]);

  const handleLanguageCancel = useCallback(() => {
    setTempLanguage(formik.values.language || ["English"]);
    formik.setFieldError("language", undefined);
    setIsLanguageEditable(false);
  }, [formik]);

  const handleLanguageBlur = useCallback(() => {
    if (tempLanguage.length === 0) {
      formik.setFieldError(
        "language",
        "At least one language must be selected"
      );
    } else {
      formik.setFieldValue("language", tempLanguage);
      localStorage.setItem("languagePreference", JSON.stringify(tempLanguage));
      formik.setFieldError("language", undefined);
      setIsLanguageEditable(false);
    }
  }, [formik, tempLanguage]);

  // Helper function to validate and process images
  const validateAndProcessImages = useCallback(
    (newImages, isBugReport = false) => {
      const currentImages = isBugReport ? bugData.images : multipleImages;
      const remainingSlots = MAX_IMAGES - currentImages.length;

      if (remainingSlots <= 0) {
        toast.error(`You can only upload a maximum of ${MAX_IMAGES} images.`);
        return [];
      }

      const validImages = [];
      const oversizedImages = [];

      newImages.forEach((file) => {
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
          oversizedImages.push(file.name);
        } else {
          validImages.push(file);
        }
      });

      if (oversizedImages.length > 0) {
        toast.error(
          `The following images exceed ${MAX_IMAGE_SIZE_KB}KB and were not added: ${oversizedImages.join(
            ", "
          )}`
        );
      }

      // Limit the number of images to the remaining slots
      const imagesToAdd = validImages.slice(0, remainingSlots);

      return imagesToAdd;
    },
    [multipleImages, bugData.images]
  );

  // Image handling functions
  const handleImagePaste = useCallback(
    (event, isBugReport = false) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      const newImages = [];
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) newImages.push(file);
        }
      }

      if (newImages.length > 0) {
        const validImages = validateAndProcessImages(newImages, isBugReport);
        if (validImages.length > 0) {
          if (isBugReport) {
            setBugData((prev) => ({
              ...prev,
              images: [
                ...prev.images,
                ...validImages.map((file) => URL.createObjectURL(file)),
              ],
            }));
          } else {
            setMultipleImages((prev) => [...prev, ...validImages]);
          }
        }
      }
    },
    [setMultipleImages, setBugData, validateAndProcessImages]
  );

  const handleImageDrop = useCallback(
    (event, isBugReport = false) => {
      event.preventDefault();
      setIsDragging(false);

      const files = event.dataTransfer.files;
      const newImages = Array.from(files).filter((file) =>
        file.type.startsWith("image/")
      );

      if (newImages.length > 0) {
        const validImages = validateAndProcessImages(newImages, isBugReport);
        if (validImages.length > 0) {
          if (isBugReport) {
            setBugData((prev) => ({
              ...prev,
              images: [
                ...prev.images,
                ...validImages.map((file) => URL.createObjectURL(file)),
              ],
            }));
          } else {
            setMultipleImages((prev) => [...prev, ...validImages]);
          }
        }
      }
    },
    [setMultipleImages, setBugData, validateAndProcessImages]
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleImageDelete = useCallback(
    (index, isBugReport = false) => {
      if (isBugReport) {
        setBugData((prev) => ({
          ...prev,
          images: prev.images.filter((_, i) => i !== index),
        }));
      } else {
        setMultipleImages((prev) => prev.filter((_, i) => i !== index));
      }
    },
    [setMultipleImages, setBugData]
  );

  const handleImageClickUpload = useCallback(
    (event, isBugReport = false) => {
      const files = event.target.files;
      const newImages = Array.from(files).filter((file) =>
        file.type.startsWith("image/")
      );

      if (newImages.length > 0) {
        const validImages = validateAndProcessImages(newImages, isBugReport);
        if (validImages.length > 0) {
          if (isBugReport) {
            setBugData((prev) => ({
              ...prev,
              images: [
                ...prev.images,
                ...validImages.map((file) => URL.createObjectURL(file)),
              ],
            }));
          } else {
            setMultipleImages((prev) => [...prev, ...validImages]);
          }
        }
      }
    },
    [setMultipleImages, setBugData, validateAndProcessImages]
  );

  const triggerFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const moduleOptions =
    profile.role === "BENCH_SALES_RECRUITER" ? allModulesBenchSale : allModule;

  return (
    <Box sx={{ px: "20px", py: "10px" }}>
      {/* Header with Close Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "25px !important", color: "#595959" }}>
            <b>100%</b>
          </Typography>
          <Typography sx={{ color: "#595959" }}>
            {formType === "bugReport"
              ? "Get number of credit on Error Acceptance."
              : "Quick solver of queries by our expert team."}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <ToggleButtonGroup
            value={formType}
            exclusive
            size="small"
            onChange={handleFormTypeChange}
            aria-label="form type"
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontSize: "14px",
                padding: "6px 16px",
                maxHeight: 35,
                borderRadius: "3px",
                mb: 0.5,
                border: "1px solid #2e75b6",
                color: "#2e75b6",
                "&.Mui-selected": {
                  backgroundColor: "#1565c0",
                  color: "#fff",
                },
              },
            }}
          >
            <ToggleButton
              size="small"
              value="helpTicket"
              aria-label="help ticket"
            >
              Ticket for Support
            </ToggleButton>
            <ToggleButton
              size="small"
              value="bugReport"
              aria-label="bug report"
            >
              Ticket for Developer
            </ToggleButton>
          </ToggleButtonGroup>
          <RxCross2
            style={{
              fontSize: "15px",
              cursor: "pointer",
              marginLeft: "10px",
              marginBottom: "20px",
            }}
            onClick={handleClose}
          />
        </Box>
      </Box>

      {/* Help Ticket Form */}
      {formType === "helpTicket" && (
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography sx={{ fontWeight: "bold" }}>
                {profile?.full_name || "Name"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  component="label"
                  htmlFor="mobile_no"
                  sx={{
                    fontSize: "14px",
                    color: "#333",
                    mr: 2,
                    mt: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  Mobile No:
                </Box>
                {isMobileEditable ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TextField
                      variant="standard"
                      name="mobile_no"
                      value={tempMobileNo}
                      onChange={(e) => setTempMobileNo(e.target.value)}
                      onBlur={handleMobileBlur}
                      InputProps={{ disableUnderline: false }}
                      sx={{ width: "150px", mr: 1 }}
                      error={!!formik.errors.mobile_no}
                      helperText={formik.errors.mobile_no}
                    />
                    <IconButton
                      size="small"
                      onClick={handleMobileSave}
                      sx={{ color: "green" }}
                    >
                      <FiCheck />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleMobileCancel}
                      sx={{ color: "red" }}
                    >
                      <FiX />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography>
                    {formik.values.mobile_no ||
                      profile?.mobile_no ||
                      "9437627244"}{" "}
                     
                    <span
                      style={{ color: "#1976d2", cursor: "pointer" }}
                      onClick={handleMobileEdit}
                    >
                      Edit
                    </span>
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Box
                  component="label"
                  htmlFor="language_preference"
                  sx={{
                    fontSize: "14px",
                    color: "#333",
                    mr: 2,
                    mt: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  Language Preference:
                </Box>
                {isLanguageEditable ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <FormControl sx={{ width: "150px", mr: 1 }}>
                      <Select
                        multiple
                        name="language"
                        value={tempLanguage}
                        onChange={(event) => {
                          const newLanguages = event.target.value;
                          setTempLanguage(newLanguages);
                        }}
                        onBlur={handleLanguageBlur}
                        variant="standard"
                        disableUnderline={false}
                        renderValue={(selected) => selected.join(", ")}
                        error={!!formik.errors.language}
                      >
                        {languages.map((lang) => (
                          <MenuItem key={lang.value} value={lang.value}>
                            {lang.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.errors.language && (
                        <Typography
                          sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}
                        >
                          {formik.errors.language}
                        </Typography>
                      )}
                    </FormControl>
                    <IconButton
                      size="small"
                      onClick={handleLanguageSave}
                      sx={{ color: "green" }}
                    >
                      <FiCheck />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleLanguageCancel}
                      sx={{ color: "red" }}
                    >
                      <FiX />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography>
                    {formik.values.language?.length > 0
                      ? formik.values.language.join(", ")
                      : "English"}{" "}
                     
                    <span
                      style={{ color: "#1976d2", cursor: "pointer" }}
                      onClick={handleLanguageEdit}
                    >
                      Edit
                    </span>
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: -0.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                    pb: 0.3,
                  }}
                >
                  <Box
                    component="label"
                    htmlFor="subject"
                    sx={{
                      fontSize: "14px",
                      color: "#333",
                      mr: 2,
                      mt: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Subject:
                  </Box>
                  <TextField
                    id="subject"
                    variant="standard"
                    name="subject"
                    value={formik.values.subject}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    fullWidth
                    InputProps={{ disableUnderline: true }}
                    sx={{ mt: 0.2 }}
                  />
                </Box>
                {formik.touched.subject && formik.errors.subject && (
                  <Box
                    sx={{
                      color: "#d32f2f",
                      fontSize: "12px",
                      mt: 0.2,
                      ml: "70px",
                    }}
                  >
                    {formik.errors.subject}
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ mt: -0.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                    pb: 0.3,
                  }}
                >
                  <Box
                    component="label"
                    htmlFor="module_names"
                    sx={{
                      fontSize: "14px",
                      color: "#333",
                      mr: 2,
                      mt: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Module Names:
                  </Box>
                  <Autocomplete
                    multiple
                    id="module_names"
                    options={moduleOptions}
                    disableCloseOnSelect
                    value={moduleOptions?.filter((mod) =>
                      formik.values.module?.includes(mod.value)
                    )}
                    onChange={(event, newValue) => {
                      formik.setFieldValue(
                        "module",
                        newValue.map((option) => option.value)
                      );
                    }}
                    onBlur={formik.handleBlur}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                        }}
                        sx={{ mt: 0.2 }}
                      />
                    )}
                    sx={{ flexGrow: 1 }}
                  />
                </Box>
                {formik.touched.module && formik.errors.module && (
                  <Box
                    sx={{
                      color: "#d32f2f",
                      fontSize: "12px",
                      mt: 0.2,
                      ml: "110px",
                    }}
                  >
                    {formik.errors.module}
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ mt: -0.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                    pb: 0.3,
                  }}
                >
                  <Box
                    component="label"
                    htmlFor="page_names"
                    sx={{
                      fontSize: "14px",
                      color: "#333",
                      mr: 2,
                      mt: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Page Names:
                  </Box>
                  <TextField
                    id="page_names"
                    variant="standard"
                    name="page_name"
                    value={formik.values.page_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    fullWidth
                    InputProps={{ disableUnderline: true }}
                    sx={{ mt: 0.2 }}
                  />
                </Box>
                {formik.touched.page_name && formik.errors.page_name && (
                  <Box
                    sx={{
                      color: "#d32f2f",
                      fontSize: "12px",
                      mt: 0.2,
                      ml: "90px",
                    }}
                  >
                    {formik.errors.page_name}
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description}
                sx={{ mb: 0.5 }}
              />
              {formik.touched.description && formik.errors.description && (
                <Typography color="error">
                  {formik.errors.description}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Box
                onClick={triggerFileInput}
                onPaste={(e) => handleImagePaste(e, false)}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleImageDrop(e, false)}
                sx={{
                  border: isDragging ? "2px dashed #1976d2" : "2px dashed #ccc",
                  borderRadius: "3px",
                  padding: "16px",
                  textAlign: "center",
                  backgroundColor: isDragging ? "#e3f2fd" : "#f5f5f5",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#e0e0e0" },
                }}
              >
                <input
                  type="file"
                  multiple
                  hidden
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={(e) => handleImageClickUpload(e, false)}
                />
                <Typography sx={{ color: "#666", fontSize: "14px" }}>
                  Drag and drop images here, paste from clipboard, or click to
                  upload (Max {MAX_IMAGES} images, {MAX_IMAGE_SIZE_KB}KB each)
                </Typography>
                {multipleImages.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexWrap: "wrap",
                      justifyContent: "center",
                      maxHeight: "200px",
                      overflowY: "auto",
                      padding: "8px",
                    }}
                  >
                    {multipleImages.map((file, index) => (
                      <Box key={index} sx={{ position: "relative" }}>
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Preview ${index + 1}`}
                          width="100"
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent triggering file input
                            handleImageDelete(index, false);
                          }}
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                            color: "#fff",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.8)",
                            },
                          }}
                        >
                          <FiX />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <button
                type="submit"
                disabled={helpdeskStatus === "loading"}
                style={{
                  backgroundColor: "#1565c0",
                  padding: "8px 16px",
                  color: "#fff",
                  border: "none",
                  minWidth: 100,
                  borderRadius: "3px",
                  cursor: "pointer",
                }}
              >
                {helpdeskStatus === "loading" ? "Loading" : "Submit"}
              </button>
            </Grid>
          </Grid>
        </form>
      )}

      {/* Bug Report Form */}
      {formType === "bugReport" && (
        <form onSubmit={handleBugSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                {profile?.full_name || "Name"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Box
                  component="label"
                  htmlFor="mobile_no"
                  sx={{
                    fontSize: "14px",
                    color: "#333",
                    mr: 2,
                    whiteSpace: "nowrap",
                  }}
                >
                  Mobile No:
                </Box>
                {isMobileEditable ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TextField
                      variant="standard"
                      name="mobile_no"
                      value={tempMobileNo}
                      onChange={(e) => setTempMobileNo(e.target.value)}
                      onBlur={handleMobileBlur}
                      InputProps={{ disableUnderline: false }}
                      sx={{ width: "150px", mr: 1 }}
                      error={!!bugData.mobile_no_error}
                      helperText={bugData.mobile_no_error}
                    />
                    <IconButton
                      size="small"
                      onClick={handleMobileSave}
                      sx={{ color: "green" }}
                    >
                      <FiCheck />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleMobileCancel}
                      sx={{ color: "red" }}
                    >
                      <FiX />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography>
                    {bugData.mobile_no || profile?.mobile_no || "9437627244"}  
                    <span
                      style={{ color: "#1976d2", cursor: "pointer" }}
                      onClick={handleMobileEdit}
                    >
                      Edit
                    </span>
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: -0.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                    pb: 0.3,
                  }}
                >
                  <Box
                    component="label"
                    htmlFor="subject"
                    sx={{
                      fontSize: "14px",
                      color: "#333",
                      mr: 2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Subject:
                  </Box>
                  <TextField
                    id="subject"
                    variant="standard"
                    name="comment"
                    value={bugData.comment}
                    onChange={handleBugChange}
                    fullWidth
                    InputProps={{ disableUnderline: true }}
                    sx={{ mt: 0.2 }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ mt: -0.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                    pb: 0.3,
                  }}
                >
                  <Box
                    component="label"
                    htmlFor="module_names"
                    sx={{
                      fontSize: "14px",
                      color: "#333",
                      mr: 2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Module Names:
                  </Box>
                  <Autocomplete
                    id="module_names"
                    options={moduleOptions}
                    value={
                      moduleOptions?.find(
                        (mod) => mod.value === bugData.module_name
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      setBugData({
                        ...bugData,
                        module_name: newValue ? newValue.value : "",
                      });
                    }}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                        }}
                        sx={{ mt: 0.2 }}
                      />
                    )}
                    sx={{ flexGrow: 1 }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ mt: -0.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                    pb: 0.3,
                  }}
                >
                  <Box
                    component="label"
                    htmlFor="page_names"
                    sx={{
                      fontSize: "14px",
                      color: "#333",
                      mr: 2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Page Names:
                  </Box>
                  <TextField
                    id="page_names"
                    variant="standard"
                    name="page_name"
                    value={bugData.page_name}
                    onChange={handleBugChange}
                    fullWidth
                    InputProps={{ disableUnderline: true }}
                    sx={{ mt: 0.2 }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="page_url"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                onChange={handleBugChange}
                value={bugData.page_url}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                onClick={triggerFileInput}
                onPaste={(e) => handleImagePaste(e, true)}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleImageDrop(e, true)}
                sx={{
                  border: isDragging ? "2px dashed #1976d2" : "2px dashed #ccc",
                  borderRadius: "3px",
                  padding: "16px",
                  textAlign: "center",
                  backgroundColor: isDragging ? "#e3f2fd" : "#f5f5f5",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#e0e0e0" },
                }}
              >
                <input
                  type="file"
                  multiple
                  hidden
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={(e) => handleImageClickUpload(e, true)}
                />
                <Typography sx={{ color: "#666", fontSize: "14px" }}>
                  Drag and drop images here, paste from clipboard, or click to
                  upload (Max {MAX_IMAGES} images, {MAX_IMAGE_SIZE_KB}KB each)
                </Typography>
                {bugData.images.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexWrap: "wrap",
                      justifyContent: "center",
                      maxHeight: "200px",
                      overflowY: "auto",
                      padding: "8px",
                    }}
                  >
                    {bugData.images.map((image, index) => (
                      <Box key={index} sx={{ position: "relative" }}>
                        <img
                          src={image}
                          alt={`Bug Image ${index + 1}`}
                          width="100"
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent triggering file input
                            handleImageDelete(index, true);
                          }}
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                            color: "#fff",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.8)",
                            },
                          }}
                        >
                          <FiX />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <button
                type="submit"
                style={{
                  backgroundColor: "#1565c0",
                  padding: "8px 16px",
                  color: "#fff",
                  border: "none",
                  minWidth: 100,
                  borderRadius: "3px",
                  cursor: "pointer",
                }}
              >
                Submit Bug Report
              </button>
            </Grid>
          </Grid>
        </form>
      )}
    </Box>
  );
};

export default DrawerContent;
